import { createReducer, getType } from "typesafe-actions";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { EntityPin, MultiPin } from "@mapmycustomers/shared/types/map";
import {
  convertLatLngLiteralToLongLat,
  convertPinRegionToLongLat,
} from "@mapmycustomers/shared/util/geo/GeoService";

import MapMode from "@app/enum/map/MapMode";
import LassoToolMode from "@app/scene/map/enums/LassoToolMode";
import ToolbarState from "@app/scene/map/store/toolbar/ToolbarState";
import LassoModel from "@app/scene/map/types/LassoModel";
import PinDropModel from "@app/scene/map/types/PinDropModel";
import MapViewportState from "@app/types/map/MapViewportState";
import RulerPath from "@app/types/map/RulerPath";
import TerritoryModeState from "@app/types/map/TerritoryModeState";
import { MAX_LASSO_PATHS } from "@app/util/lasso";
import loggingService from "@app/util/logging";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { getRulerPinId } from "@app/util/map/getRulerPinId";

import {
  Actions,
  appendLassoPath,
  cleanLassoPaths,
  enterMode,
  exitLassoMode,
  exitMode,
  fetchLassoSelection,
  GroupModeActions,
  initializeMapView,
  MapModeActions,
  PinLocationActions,
  selectMapTool,
  setLassoContainsFlags,
  setLocationSearchVisibility,
  setMapSettings,
  setPinDropPlace,
  TerritoryModeActions,
  toggleRulerPin,
  ToolbarActions,
  updatePinDropCoordinates,
  updateRulerPath,
  updateViewportState,
} from "./actions";
import GroupModeState from "./groupMode/GroupModeState";
import groups, { groupsModeInitialState } from "./groupMode/reducers";
import MapModeState from "./mapMode/MapModeState";
import mapMode, { mapModeInitialState } from "./mapMode/reducers";
import PinLocationState from "./pinLocation/PinLocationState";
import pinLocation, { pinLocationInitialState } from "./pinLocation/reducers";
import territory, { territoryModeInitialState } from "./territoryMode/reducers";
import toolbar, { toolbarInitialState } from "./toolbar/reducers";

export interface MapState {
  error: undefined | unknown;
  groupMode: GroupModeState;
  initializing: boolean;
  lasso: LassoModel;
  loading: boolean;
  locationSearchVisible: boolean;
  mapMode: MapModeState;
  mode: MapMode | undefined;
  pinDrop?: PinDropModel;
  pinLocation: PinLocationState;
  ruler: {
    path: RulerPath;
  };
  territoryMode: TerritoryModeState;
  toolbar: ToolbarState;
  viewport: MapViewportState;
}

const initialState: MapState = {
  error: undefined,
  groupMode: groupsModeInitialState,
  initializing: false,
  lasso: {
    allowAdd: true,
    containsClusters: false,
    loading: false,
    options: {
      mode: LassoToolMode.DEFAULT,
      visibleEntityTypes: MAP_ENTITY_TYPES,
    },
    paths: [],
  },
  loading: false,
  locationSearchVisible: false,
  mapMode: mapModeInitialState,
  mode: undefined,
  pinDrop: undefined,
  pinLocation: pinLocationInitialState,
  ruler: {
    path: [],
  },
  territoryMode: territoryModeInitialState,
  toolbar: toolbarInitialState,
  viewport: {
    bounds: { bottom_right: [0, 0], top_left: [0, 0] },
    zoom: 2,
  },
};

const map = createReducer<MapState, Actions>(initialState)
  .handleType(getType(initializeMapView.request), (state) => ({
    ...state,
    initializing: true,
  }))
  .handleType(getType(setMapSettings), (state, { payload }) => ({
    ...state,
    ...payload,
  }))
  .handleType(
    [getType(initializeMapView.success), getType(initializeMapView.failure)],
    (state) => ({
      ...state,
      initializing: false,
    })
  )
  .handleType(getType(updateViewportState), (state, { payload }) => ({
    ...state,
    viewport: payload,
  }))
  .handleType(getType(enterMode), (state, { payload }) => ({
    ...state,
    mode: payload,
  }))
  .handleType(getType(exitMode), (state) => ({
    ...state,
    mode: undefined,
  }))
  .handleType(getType(selectMapTool.success), (state, { payload }) => {
    let mapTool = payload === undefined ? undefined : payload.mapTool;
    let lassoMode = state.lasso.options.mode;

    if (
      payload?.mapTool === MapTool.LASSO &&
      payload.mapToolOptions?.mode === LassoToolMode.ADD_GROUP_PINS
    ) {
      // only allow switching into ADD_GROUP_PINS lasso mode when the conditions are met
      if (
        payload.mapToolOptions?.addItemsToGroupId &&
        payload.mapToolOptions?.visibleEntityTypes.length === 1
      ) {
        lassoMode = LassoToolMode.ADD_GROUP_PINS;
      } else {
        loggingService.error(
          "Can not switch into AddToGroup lasso mode, conditions are not met. Check options:",
          payload.mapToolOptions
        );
        mapTool = undefined;
        lassoMode = LassoToolMode.DEFAULT;
      }
    }

    return {
      ...state,
      lasso:
        payload?.mapTool === MapTool.LASSO
          ? {
              ...state.lasso,
              options: {
                ...state.lasso.options,
                addItemsToGroupId:
                  lassoMode === LassoToolMode.ADD_GROUP_PINS
                    ? payload.mapToolOptions?.addItemsToGroupId
                    : undefined,
                mode: lassoMode,
                visibleEntityTypes:
                  lassoMode !== LassoToolMode.DEFAULT
                    ? (payload.mapToolOptions?.visibleEntityTypes ?? MAP_ENTITY_TYPES)
                    : MAP_ENTITY_TYPES,
              },
            }
          : state.lasso,
      mapMode: {
        ...state.mapMode,
        viewState: {
          ...state.mapMode.viewState,
          tool: mapTool,
        },
      },
      ruler: {
        ...state.ruler,
        path: mapTool === undefined ? [] : state.ruler.path,
      },
    };
  })
  .handleType(getType(updatePinDropCoordinates.request), (state, { payload }) => ({
    ...state,
    pinDrop: {
      ...state.pinDrop,
      address: undefined,
      coordinates: payload,
      loading: true,
      lookupFailed: undefined,
      place:
        payload &&
        payload.latitude === state.pinDrop?.coordinates?.latitude &&
        payload.longitude === state.pinDrop?.coordinates?.longitude
          ? state.pinDrop.place
          : undefined,
      visible: !!payload,
    },
  }))
  .handleType(getType(updatePinDropCoordinates.success), (state, { payload }) => ({
    ...state,
    pinDrop: {
      ...state.pinDrop,
      address: payload,
      loading: false,
      lookupFailed: undefined,
    },
  }))
  .handleType(getType(updatePinDropCoordinates.failure), (state) => ({
    ...state,
    pinDrop: {
      ...state.pinDrop,
      address: undefined,
      loading: false,
      lookupFailed: true,
    },
  }))
  .handleType(getType(setPinDropPlace), (state, { payload }) => ({
    ...state,
    pinDrop: {
      ...state.pinDrop,
      place: payload,
    },
  }))
  .handleType(getType(appendLassoPath), (state, { payload }) => ({
    ...state,
    lasso: {
      ...state.lasso,
      allowAdd:
        (state.lasso?.paths && Array.isArray(state.lasso.paths) ? state.lasso.paths.length : 0) <
        MAX_LASSO_PATHS - 1,
      paths:
        state.lasso?.paths && Array.isArray(state.lasso.paths)
          ? [...state.lasso.paths, payload]
          : [payload],
    },
  }))
  .handleType(getType(cleanLassoPaths), (state) => ({
    ...state,
    lasso: {
      ...state.lasso,
      allowAdd: true,
      containsClusters: false,
      paths: [],
    },
  }))
  .handleType(getType(setLassoContainsFlags), (state, { payload }) => ({
    ...state,
    lasso: {
      ...state.lasso,
      ...payload,
    },
  }))
  .handleType(getType(exitLassoMode), (state) => ({
    ...state,
    lasso: {
      ...state.lasso,
      options: {
        ...state.lasso.options,
        mode: LassoToolMode.DEFAULT,
      },
    },
  }))
  .handleType(getType(fetchLassoSelection.request), (state) => ({
    ...state,
    lasso: {
      ...state.lasso,
      loading: true,
    },
  }))
  .handleType(
    [getType(fetchLassoSelection.success), getType(fetchLassoSelection.failure)],
    (state) => ({
      ...state,
      lasso: {
        ...state.lasso,
        loading: false,
      },
    })
  )
  .handleType(getType(updateRulerPath), (state, { payload }) => ({
    ...state,
    ruler: { ...state.ruler, path: payload },
  }))
  .handleType(getType(toggleRulerPin), (state, { payload }) => {
    let path = state.ruler.path;
    const pinId = getRulerPinId(payload);
    const pinCoordinates =
      "place_id" in payload
        ? convertLatLngLiteralToLongLat((payload as Lead).geometry.location)
        : convertPinRegionToLongLat((payload as EntityPin | MultiPin).region);

    const pinIndex = path.findIndex((point) => getRulerPinId(point.pin) === pinId);
    if (pinIndex > -1) {
      // pin already added, let's remove
      path = path.toSpliced(pinIndex, 1);
    } else if (pinCoordinates) {
      // pin not yet added, let's append
      path = [...path, { coordinates: pinCoordinates, pin: payload }];
    }
    return { ...state, ruler: { ...state.ruler, path } };
  })
  .handleType(
    Object.keys(mapMode.handlers) as MapModeActions["type"][],
    (state: MapState, action: MapModeActions) => ({
      ...state,
      mapMode: mapMode(state.mapMode, action),
    })
  )
  .handleType(
    Object.keys(groups.handlers) as GroupModeActions["type"][],
    (state: MapState, action: GroupModeActions) => ({
      ...state,
      groupMode: groups(state.groupMode, action),
    })
  )
  .handleType(
    Object.keys(territory.handlers) as TerritoryModeActions["type"][],
    (state: MapState, action: TerritoryModeActions) => ({
      ...state,
      territoryMode: territory(state.territoryMode, action),
    })
  )
  .handleType(getType(setLocationSearchVisibility), (state, { payload }) => ({
    ...state,
    locationSearchVisible: payload,
  }))
  .handleType(
    Object.keys(pinLocation.handlers) as PinLocationActions["type"][],
    (state: MapState, action: PinLocationActions) => ({
      ...state,
      pinLocation: pinLocation(state.pinLocation, action),
    })
  )
  .handleType(
    Object.keys(toolbar.handlers) as ToolbarActions["type"][],
    (state: MapState, action: ToolbarActions) => ({
      ...state,
      toolbar: toolbar(state.toolbar, action),
    })
  );

export type MapActions = Actions;
export default map;
